import {map, get, keys, isEmpty, omit, findIndex, sortBy} from 'lodash';
import {useCallback, useState, useEffect, useMemo, Fragment, useContext} from 'react';
import {useTranslation} from 'react-i18next';

import {rolesApi} from 'api';
import {notifier, PageHeader, Icon, Card, ActionButton, PredefinedNotifierType, DataWrapper, getMenuItems} from 'components';
import {ConfirmModal} from 'components/modals';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {UserPermissions, RolePermission, BaseUserRole, PagePermission, ApiError} from 'types';
import {buildTranslationKey, getRoleName} from 'utils/transform';

import {PermissionList} from './PermissionList';
import {RoleModal} from './RoleModal';
import {ROLE_ERROR} from './constants';

const T_KEYS_PAGE_NAMES = TRANSLATION_KEYS.USER.PAGE_NAMES;
const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.ROLES;

const DEFAULT_ROLES: RolePermission[] = [
    {
        id: BaseUserRole.Admin as string,
        name: 'Admin',
        editable: false,
        permissions: {
            all: {enabled: true}
        }
    },
    {
        id: BaseUserRole.Agent as string,
        name: 'Agent',
        editable: false,
        permissions: {
            online: {enabled: true},
            account: {
                enabled: true,
                myProfile: {enabled: true}
            }
        }
    }
];

export const Roles: React.FC = () => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const clientConnectionsEnabled = useMemo(() => settings.featuresAvailability.clientsConnections, [settings]);

    const [roleModalProps, setRoleModalProps] = useState<{
        open: boolean;
        editMode?: boolean;
        role?: {
            id?: string;
            name: string;
            permissions: UserPermissions
        },
        error?: ApiError;
    }>({
        open: false,
    });
    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState({
        open: false,
        role: null
    });

    const menuItems = useMemo(() => getMenuItems({
        t, clientConnectionsEnabled, incomingSessionCount: 0
    }), [t, clientConnectionsEnabled]);

    const {
        send: fetchRolePermissions,
        data: editableRoles,
        loading: rolePermissionLoading
    } = useApiData<RolePermission[]>(rolesApi.getRolesPermissions, {
        loadingDefault: true, notificationType: PredefinedNotifierType.FetchError
    });

    const rolePermissions: RolePermission[] = useMemo(() => [
        ...DEFAULT_ROLES,
        ...(map(editableRoles, (role) => ({...role, editable: true})))
    ], [editableRoles]);

    const {
        send: fetchAvailablePermissions,
        data: availablePermissions
    } = useApiData<UserPermissions>(rolesApi.getAvailablePermissions, {notificationType: PredefinedNotifierType.FetchError});

    const addNewRole = useCallback(() => {
        setRoleModalProps({
            open: true,
            role: {
                name: '',
                permissions: {}
            },
            editMode: false,
            error: null
        });
    }, [setRoleModalProps]);

    const openEditRoleModal = useCallback((item) => {
        setRoleModalProps({
            open: true,
            role: item,
            editMode: true,
            error: null
        });
    }, [setRoleModalProps]);

    const onRoleModalClose = useCallback(async (confirm: boolean, data: RolePermission) => {
        if (!confirm || !data) {
            setRoleModalProps({
                ...roleModalProps,
                open: false,
                error: null
            });
            return;
        }
        try {
            if (roleModalProps.editMode) {
                await rolesApi.updateRole(data);
            } else {
                await rolesApi.createRole(data);
            }
            await fetchRolePermissions();
            setRoleModalProps({
                ...roleModalProps,
                open: false,
                error: null
            });
        } catch (error: any) {
            setRoleModalProps({
                ...roleModalProps,
                error
            });
        }
    }, [roleModalProps, fetchRolePermissions]);

    const onRemoveConfirmModalClose = useCallback(async (confirm: boolean) => {
        if (confirm && confirmDeleteModalProps.role) {
            try {
                await rolesApi.removeRole(confirmDeleteModalProps.role.id);
                await fetchRolePermissions();
            } catch (error: any) {
                const errorDescription = error?.response?.data?.errors?.[0];
                const isSingleRoleError = errorDescription === ROLE_ERROR.DELETE_SINGLE_USER_ROLE;
                if (isSingleRoleError) {
                    notifier.error({title: t(T_KEYS.ERRORS.SINGLE_ROLE)});
                } else {
                    notifier.error({title: t(T_KEYS.ERRORS.UNEXPECTED)});
                }
            } finally {
                setConfirmDeleteModalProps({open: false, role: null});
            }
        }
        setConfirmDeleteModalProps({open: false, role: null});
    }, [confirmDeleteModalProps, setConfirmDeleteModalProps, fetchRolePermissions, t]);

    const openRemoveModal = useCallback((item) => {
        setConfirmDeleteModalProps({
            open: true,
            role: item
        });
    }, [setConfirmDeleteModalProps]);

    const getTKey = useCallback((path: string, options: {selfAdded: boolean}) => {
        const formattedPath = map(path?.split('.'), buildTranslationKey).join('.');
        const tKey = `${formattedPath}${options.selfAdded ? '.SELF' : ''}`;
        return get(T_KEYS_PAGE_NAMES, tKey) || tKey;
    }, []);

    const getFormattedPermissions = useCallback((node: UserPermissions | PagePermission, path = '') => {
        const permissions = keys(node)
            .filter((key) => key !== 'enabled')
            .map((key) => {
                const newPath = path ? path.concat('.', key) : key;
                const selfAdded = !isEmpty(omit(get(availablePermissions, newPath), 'enabled'));
                const children = getFormattedPermissions(node[key], newPath);
                return ({
                    name: key,
                    path: newPath,
                    tKey: getTKey(newPath, {selfAdded}),
                    label: t(getTKey(newPath, {selfAdded})),
                    ...((node[key] as PagePermission)?.enabled && {enabled: (node[key] as PagePermission)?.enabled}),
                    ...(!isEmpty(children) && {children})
                });
            });

        let source = menuItems;
        if (path) {
            const tKey = getTKey(path, {selfAdded: true});
            const menuItem = menuItems.find((item) => item.titleTKey === tKey);
            if (menuItem) {
                source = menuItem.subItems;
            }
        }

        return sortBy(
            permissions,
            ({tKey}) => findIndex(source, (item) => item.titleTKey === tKey));
    }, [availablePermissions, t, menuItems, getTKey]);

    useEffect(() => {
        fetchRolePermissions();
        fetchAvailablePermissions();
    }, [fetchRolePermissions, fetchAvailablePermissions]);

    return (
        <div>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />

            <div className='tw-my-4'>
                <ActionButton action='create' disabled={rolePermissionLoading} onClick={addNewRole}>
                    {t(T_KEYS.ADD_ROLE_BTN)}
                </ActionButton>
            </div>

            <div className='tw-relative'>
                <DataWrapper isLoading={rolePermissionLoading}>
                    {map(rolePermissions, (role, roleIndex) => (
                        <Card key={roleIndex} className='tw-my-2'>
                            <div className='tw-flex tw-flex-wrap tw-min-h-8 -lg-landscape:tw-grid -lg-landscape:tw-grid-cols-5'>
                                <h4 className='tw-ts-h5-bold'>{getRoleName(t, role.id, rolePermissions)}</h4>
                                <PermissionList
                                    availablePermissions={availablePermissions}
                                    formattedPermissions={getFormattedPermissions(role.permissions)}
                                    getFormattedPermissions={getFormattedPermissions}
                                />
                                <div className='tw-flex tw-text-right tw-ml-auto'>
                                    {role.editable ?
                                        <Fragment>
                                            <Icon
                                                button
                                                group
                                                name='edit'
                                                color='primary'
                                                onClick={() => openEditRoleModal(role)}
                                            />
                                            <Icon
                                                button
                                                group
                                                name='delete'
                                                color='alert'
                                                onClick={() => openRemoveModal(role)}
                                            />
                                        </Fragment> : <Icon name='lock' />}
                                </div>
                            </div>
                        </Card>
                    ))}
                </DataWrapper>
            </div>

            <RoleModal
                {...roleModalProps}
                availablePermissions={availablePermissions}
                getFormattedPermissions={getFormattedPermissions}
                onClose={onRoleModalClose}
            />

            <ConfirmModal
                open={confirmDeleteModalProps.open}
                title={t(T_KEYS.CONFIRM_REMOVE)}
                onClose={onRemoveConfirmModalClose}
            />

        </div>
    );
};
